/**
 * Os vínculos explícitos da tela "Contratos digitais" (E177.F4.T04, GUS-253, ADR-0130 §2.3; HTML 6.8
 * por CCB `?tela=vinculo-ccb`). Um arquivo vale para outra CCB do mesmo envio só pelo vínculo que a
 * pessoa grava:
 *
 * - **no arquivo recebido** — "Vincular a outras CCBs" (ou "Editar vínculos") abre "Aplicar <arquivo>
 *   a", com a CCB de origem marcada e fixa e as outras para marcar;
 * - **no bloco** — "Vincular documento já enviado em outra CCB" lista os arquivos das outras CCBs que
 *   ainda não valem para esta;
 * - **sem CCB** — o arquivo enviado antes das CCBs: vincular às CCBs a que pertence, ou remover.
 *
 * "Confirmar vínculos" grava só a diferença (vincular o que entrou, desvincular o que saiu) e relê.
 *
 * E177.F4.C02 (F-E177.F4-02): cada arquivo a escolher pode ser aberto antes do vínculo, e o homônimo
 * de outro documento leva o identificador curto (`nomeDoArquivo`).
 */
import { useState, type ReactNode } from 'react';
import type { AxiosInstance } from 'axios';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { UnidadeDoEnvioData } from '@/lib/abertura_do_caso';
import { deleteDocument } from '@/lib/case_source_documents';
import { desvincularDocumento, vincularDocumento } from '@/lib/ccbs_da_abertura';

import { AbrirArquivo } from './AbrirArquivo';
import { nomeDoArquivo, type BlocoDaCcb } from './blocosDasCcbs';
import { LINK_DA_CCB } from './LinhasDaCcb';
import { AvisoDaAbertura } from './PecasDaAbertura';
import { VincularTodos } from './VincularTodos';

const t = ptBR.abertura.contratosDigitais;

const rotulo = (numero: string): string => t.rotulo.replace('{numero}', numero);

/** As CCBs gravadas a que o arquivo já vale por vínculo. */
function vinculadoEm(blocos: readonly BlocoDaCcb[], documentoId: string): Set<string> {
  return new Set(
    blocos
      .filter(
        (b) => b.ccb !== null && b.vinculadas.some((v) => v.unidade.documento_id === documentoId),
      )
      .map((b) => b.chave),
  );
}

async function gravarDiferenca(
  client: AxiosInstance,
  caseId: string,
  documentoId: string,
  antes: ReadonlySet<string>,
  depois: ReadonlySet<string>,
): Promise<void> {
  for (const ccb of depois) {
    if (!antes.has(ccb)) await vincularDocumento(client, caseId, ccb, documentoId);
  }
  for (const ccb of antes) {
    if (!depois.has(ccb)) await desvincularDocumento(client, caseId, ccb, documentoId);
  }
}

function useGravacao(onRecarregar: () => Promise<void>) {
  const [gravando, setGravando] = useState(false);
  const [falhou, setFalhou] = useState(false);
  async function gravar(acao: () => Promise<void>): Promise<boolean> {
    setGravando(true);
    setFalhou(false);
    try {
      await acao();
      await onRecarregar();
      return true;
    } catch {
      setFalhou(true);
      return false;
    } finally {
      setGravando(false);
    }
  }
  return { gravando, falhou, gravar };
}

function Painel({
  legenda,
  ajuda,
  children,
  gravando,
  falhou,
  onConfirmar,
  onCancelar,
  rotuloDoCancelar = t.cancelar,
  semEscolha = false,
  testId,
}: {
  legenda: string;
  ajuda?: string;
  children: ReactNode;
  gravando: boolean;
  falhou: boolean;
  onConfirmar: () => void;
  onCancelar: () => void;
  rotuloDoCancelar?: string;
  /** Nada marcado: não há o que confirmar. */
  semEscolha?: boolean;
  testId: string;
}): JSX.Element {
  return (
    <fieldset className="mt-2 w-full border border-border bg-surface-2 p-3" data-testid={testId}>
      <legend className="px-1 text-[13px] font-semibold text-fg">{legenda}</legend>
      {ajuda !== undefined ? <p className="mb-2 text-[13px] text-fg-muted">{ajuda}</p> : null}
      <div className="flex flex-col gap-2">{children}</div>
      {falhou ? (
        <p role="alert" className="mt-2 text-[13px] text-danger">
          {t.erroAoVincular}
        </p>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-3">
        <Button
          type="button"
          onClick={onConfirmar}
          disabled={gravando || semEscolha}
          data-testid={`${testId}-confirmar`}
        >
          {t.confirmarVinculos}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancelar}
          disabled={gravando}
          data-testid={`${testId}-cancelar`}
        >
          {rotuloDoCancelar}
        </Button>
      </div>
    </fieldset>
  );
}

function Opcao({
  rotulo: texto,
  marcada,
  fixa = false,
  onMudar,
  testId,
}: {
  rotulo: string;
  marcada: boolean;
  fixa?: boolean;
  onMudar?: (marcada: boolean) => void;
  testId?: string;
}): JSX.Element {
  return (
    <label className="flex items-center gap-2 text-[14px] text-fg">
      <input
        type="checkbox"
        checked={marcada}
        disabled={fixa}
        onChange={(evt) => onMudar?.(evt.target.checked)}
        className="h-4 w-4 accent-cyan"
        data-testid={testId}
      />
      {texto}
    </label>
  );
}

export interface VinculosDoArquivoProps {
  client: AxiosInstance;
  caseId: string;
  bloco: BlocoDaCcb;
  blocos: readonly BlocoDaCcb[];
  documentoId: string;
  onRecarregar: () => Promise<void>;
}

/** "Vincular a outras CCBs" no arquivo recebido do bloco. */
export function VinculosDoArquivo(props: VinculosDoArquivoProps): JSX.Element | null {
  const { client, caseId, bloco, blocos, documentoId } = props;
  const [marcadas, setMarcadas] = useState<Set<string> | null>(null);
  const gravacao = useGravacao(props.onRecarregar);
  const outras = blocos.filter((b) => b.ccb !== null && b.chave !== bloco.chave);
  if (bloco.ccb === null || outras.length === 0) return null;
  const antes = vinculadoEm(blocos, documentoId);
  const unidade = bloco.unidades.find((u) => u.documento_id === documentoId);
  const nome = unidade !== undefined ? nomeDoArquivo(blocos, unidade) : '';
  if (marcadas === null) {
    return (
      <button
        type="button"
        className={LINK_DA_CCB}
        onClick={() => setMarcadas(new Set(antes))}
        data-testid={`ccb-vincular-${documentoId}`}
      >
        {antes.size > 0 ? t.editarVinculos : t.vincularAOutras}
      </button>
    );
  }
  const confirmar = async (): Promise<void> => {
    const ok = await gravacao.gravar(() =>
      gravarDiferenca(client, caseId, documentoId, antes, marcadas),
    );
    if (ok) setMarcadas(null);
  };
  return (
    <Painel
      legenda={t.aplicarA.replace('{arquivo}', nome)}
      ajuda={t.aplicarAjuda}
      gravando={gravacao.gravando}
      falhou={gravacao.falhou}
      onConfirmar={() => void confirmar()}
      onCancelar={() => setMarcadas(null)}
      testId={`ccb-painel-${documentoId}`}
    >
      <Opcao rotulo={t.origemDoEnvio.replace('{ccb}', rotulo(bloco.ccb.numero))} marcada fixa />
      {outras.map((o) => (
        <Opcao
          key={o.chave}
          rotulo={rotulo(o.ccb?.numero ?? '')}
          marcada={marcadas.has(o.chave)}
          onMudar={(sim) => {
            const nova = new Set(marcadas);
            if (sim) nova.add(o.chave);
            else nova.delete(o.chave);
            setMarcadas(nova);
          }}
          testId={`ccb-painel-${documentoId}-${o.chave}`}
        />
      ))}
    </Painel>
  );
}

export interface VincularJaEnviadoProps {
  client: AxiosInstance;
  caseId: string;
  bloco: BlocoDaCcb;
  blocos: readonly BlocoDaCcb[];
  onRecarregar: () => Promise<void>;
}

/** "Vincular documento já enviado em outra CCB", no fim do bloco. */
export function VincularJaEnviado(props: VincularJaEnviadoProps): JSX.Element | null {
  const { client, caseId, bloco, blocos } = props;
  const [marcados, setMarcados] = useState<Set<string> | null>(null);
  const gravacao = useGravacao(props.onRecarregar);
  if (bloco.ccb === null) return null;
  const aqui = new Set(bloco.vinculadas.map((v) => v.unidade.documento_id));
  const dasOutras = blocos.flatMap((b) =>
    b.ccb !== null && b.chave !== bloco.chave
      ? b.unidades
          .filter((u) => !aqui.has(u.documento_id))
          .map((u) => ({ unidade: u, de: b.ccb?.numero ?? '' }))
      : [],
  );
  if (!blocos.some((b) => b.chave !== bloco.chave && b.unidades.length > 0)) return null;
  if (marcados === null) {
    return (
      <button
        type="button"
        className={`${LINK_DA_CCB} mt-3`}
        onClick={() => setMarcados(new Set())}
        data-testid={`ccb-vincular-ja-enviado-${bloco.chave}`}
      >
        {t.vincularJaEnviado}
      </button>
    );
  }
  const ccbId = bloco.chave;
  const confirmar = async (): Promise<void> => {
    const ok = await gravacao.gravar(async () => {
      for (const documentoId of marcados)
        await vincularDocumento(client, caseId, ccbId, documentoId);
    });
    if (ok) setMarcados(null);
  };
  return (
    <Painel
      legenda={t.jaEnviados}
      gravando={gravacao.gravando}
      falhou={gravacao.falhou}
      onConfirmar={() => void confirmar()}
      onCancelar={() => setMarcados(null)}
      semEscolha={marcados.size === 0}
      testId={`ccb-ja-enviados-${bloco.chave}`}
    >
      {dasOutras.length === 0 ? (
        <p className="text-[13px] text-fg-muted">{t.todosVinculados}</p>
      ) : (
        dasOutras.map(({ unidade, de }) => (
          <div
            key={unidade.documento_id}
            className="flex flex-wrap items-center justify-between gap-3"
          >
            <Opcao
              rotulo={`${nomeDoArquivo(blocos, unidade)} · ${rotulo(de)}`}
              marcada={marcados.has(unidade.documento_id)}
              onMudar={(sim) => {
                const novo = new Set(marcados);
                if (sim) novo.add(unidade.documento_id);
                else novo.delete(unidade.documento_id);
                setMarcados(novo);
              }}
              testId={`ccb-ja-enviado-${bloco.chave}-${unidade.documento_id}`}
            />
            <AbrirArquivo
              client={client}
              caseId={caseId}
              documentoId={unidade.documento_id}
              nome={nomeDoArquivo(blocos, unidade)}
            />
          </div>
        ))
      )}
    </Painel>
  );
}

function ArquivoSemCcb({
  client,
  caseId,
  unidade,
  blocos,
  onRecarregar,
}: {
  client: AxiosInstance;
  caseId: string;
  unidade: UnidadeDoEnvioData;
  blocos: readonly BlocoDaCcb[];
  onRecarregar: () => Promise<void>;
}): JSX.Element {
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const gravacao = useGravacao(onRecarregar);
  const gravadas = blocos.filter((b) => b.ccb !== null);
  const nome = nomeDoArquivo(blocos, unidade);
  return (
    <Painel
      legenda={nome}
      gravando={gravacao.gravando}
      falhou={gravacao.falhou}
      onConfirmar={() =>
        void gravacao.gravar(() =>
          gravarDiferenca(client, caseId, unidade.documento_id, new Set(), marcadas),
        )
      }
      onCancelar={() => void gravacao.gravar(() => deleteDocument(client, unidade.documento_id))}
      rotuloDoCancelar={t.remover}
      semEscolha={marcadas.size === 0}
      testId={`ccb-sem-ccb-${unidade.documento_id}`}
    >
      <div>
        <AbrirArquivo
          client={client}
          caseId={caseId}
          documentoId={unidade.documento_id}
          nome={nome}
        />
      </div>
      {gravadas.map((b) => (
        <Opcao
          key={b.chave}
          rotulo={rotulo(b.ccb?.numero ?? '')}
          marcada={marcadas.has(b.chave)}
          onMudar={(sim) => {
            const nova = new Set(marcadas);
            if (sim) nova.add(b.chave);
            else nova.delete(b.chave);
            setMarcadas(nova);
          }}
          testId={`ccb-sem-ccb-${unidade.documento_id}-${b.chave}`}
        />
      ))}
    </Painel>
  );
}

/** Os arquivos enviados antes das CCBs: vincular às CCBs a que pertencem, ou remover. */
export function ArquivosSemCcb(props: {
  client: AxiosInstance;
  caseId: string;
  soltos: readonly UnidadeDoEnvioData[];
  blocos: readonly BlocoDaCcb[];
  onRecarregar: () => Promise<void>;
}): JSX.Element | null {
  if (props.soltos.length === 0) return null;
  return (
    <AvisoDaAbertura
      tom="aviso"
      titulo={t.semCcbTitulo}
      texto={t.semCcbTexto}
      testId="ccbs-sem-ccb"
    >
      {/* E209.F3.T03 (GUS-296 item 12b): todos de uma vez, além da escolha por arquivo. */}
      <VincularTodos
        client={props.client}
        caseId={props.caseId}
        soltos={props.soltos}
        blocos={props.blocos}
        onRecarregar={props.onRecarregar}
      />
      {props.soltos.map((u) => (
        <ArquivoSemCcb
          key={u.documento_id}
          client={props.client}
          caseId={props.caseId}
          unidade={u}
          blocos={props.blocos}
          onRecarregar={props.onRecarregar}
        />
      ))}
    </AvisoDaAbertura>
  );
}
